import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings, Plus, Pencil, Trash2, X, AlertTriangle, Landmark } from "lucide-react";
import { api } from "../../lib/api";
import { isPermissionActionExpired } from "../../context/AuthContext";

/* ============================================================================
 * SATU SUMBER untuk metode pembayaran (dropdown + layar kelola).
 *
 * KENAPA berkas ini ada: modal "Kelola Metode Pembayaran" pernah TERSALIN
 * PERSIS di InputNota.jsx dan InputNotaAkuisisi.jsx (beda cuma warna tombol &
 * akhiran data-testid), sementara Sales.jsx — halaman yang paling sering
 * dipakai kasir — tidak punya tombolnya sama sekali. Dua salinan yang sama
 * pasti berpisah jalan; itu sudah terjadi pada `PaymentMethodSelect`, yang di
 * Sales.jsx sudah bisa menangani nilai metode lama sementara dua salinan
 * lainnya belum (lihat CLAUDE.md: "kalau butuh sesuatu di dua tempat, jadikan
 * satu sumber").
 * ========================================================================== */

/* ---------------------------------------------------------------------------
 * Gerbang izin — HARUS cermin `payment_methods_module._boleh_kelola` di backend.
 *
 * Sengaja TIDAK memakai `can()` biasa: `can()` jatuh ke default role generik,
 * yang memberi semua role finance_* (selain staff/auditor) akses penuh ke modul
 * finance. Backend justru sengaja lebih sempit di sini karena endpoint ini
 * menentukan ke REKENING MANA uang penjualan mendarat. Kalau gerbang layar
 * lebih longgar dari backend, finance_spv akan melihat tombol yang selalu
 * berakhir 403 (melanggar CLAUDE.md #3). Pola & alasan sama dengan
 * `pages/finance/FinancePettyCash.jsx`.
 * ------------------------------------------------------------------------- */
export const MODUL_IZIN_METODE = "finance_pengaturan";

function izinEksplisit(user, module, action) {
  if (!user) return false;
  const perms = user.permissions || {};
  const row = perms[module];
  if (row && typeof row === "object" && Object.prototype.hasOwnProperty.call(row, action)) {
    return !!row[action] && !isPermissionActionExpired(row, action);
  }
  if (typeof module === "string" && module.startsWith("finance_")) {
    const legacy = perms.finance;
    if (legacy && typeof legacy === "object" && Object.prototype.hasOwnProperty.call(legacy, action)) {
      return !!legacy[action] && !isPermissionActionExpired(legacy, action);
    }
  }
  return false;
}

/** Role lama SAMA PERSIS dengan `_ROLE_LAMA_KELOLA` di backend (aditif). */
export const bolehKelolaMetode = (user) =>
  ["superadmin", "admin"].includes(user?.role)
  || izinEksplisit(user, MODUL_IZIN_METODE, "update");

/* ---------------------------------------------------------------------------
 * Dropdown metode pembayaran.
 *
 * Transaksi LAMA bisa menyimpan nilai ringkas ("Transfer", "Debit", "EDC")
 * yang tidak punya padanan persis di koleksi. Kalau nilai itu tidak ada di
 * `groups`, ia disisipkan apa adanya — supaya membuka transaksi lama tidak
 * diam-diam "memindahkan" pilihannya ke opsi pertama, dan menyimpan ulang tanpa
 * menyentuh field ini tidak mengubah data keuangan yang sudah benar.
 * ------------------------------------------------------------------------- */
const KELAS_SELECT_BAWAAN = "h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white w-full";

/* ---------------------------------------------------------------------------
 * Kredit Retur (K0-2, batch UANG-KASIR 23 Sep 2026).
 *
 * Ejaan HARUS sama dengan `payment_methods_module.NAMA_METODE_KREDIT_RETUR` di
 * backend (dijaga tes `test_k0_kredit_retur_tunggal.py`), dan pencocokannya
 * cermin `sales_posting._adalah_kredit_retur` (huruf kecil + rapikan spasi) —
 * ejaan kedua yang berbeda membuat layar menyembunyikan metode yang backend
 * anggap lain, atau sebaliknya.
 * ------------------------------------------------------------------------- */
export const NAMA_METODE_KREDIT_RETUR = "Kredit Retur";
export const adalahKreditRetur = (nama) =>
  (nama || "").trim().toLowerCase().split(/\s+/).join(" ")
  === NAMA_METODE_KREDIT_RETUR.toLowerCase();

/* `sembunyikan` (opsional): predikat nama metode yang TIDAK boleh ditawarkan di
 * konteks ini (mis. Kredit Retur untuk nota bertempo / form edit — backend
 * menolaknya). `dikenal` dihitung SESUDAH saringan: nilai terpilih yang kini
 * tersembunyi harus tetap tampil apa adanya, bukan diam-diam dirender sebagai
 * opsi pertama sementara state & payload masih membawa nilai lamanya. */
export function PaymentMethodSelect({ value, onChange, groups, testid, className, sembunyikan }) {
  const tampil = Object.entries(groups || {})
    .map(([g, list]) => [g, sembunyikan ? list.filter(m => !sembunyikan(m.name)) : list])
    .filter(([, list]) => list.length > 0);
  const dikenal = tampil.some(([, list]) => list.some(m => m.name === value));
  const tersembunyi = !!(value && sembunyikan && sembunyikan(value));
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      data-testid={testid} className={className || KELAS_SELECT_BAWAAN}>
      {Object.keys(groups || {}).length === 0 && !value && <option value="Cash">Cash</option>}
      {!value && Object.keys(groups || {}).length > 0 && <option value="">— pilih metode —</option>}
      {value && !dikenal && (
        <option value={value}>{value} {tersembunyi ? "(tidak berlaku di sini)" : "(nilai lama)"}</option>
      )}
      {tampil.map(([g, list]) => (
        <optgroup key={g} label={g}>
          {list.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

/** Muat metode pembayaran sekali: aktif-saja (dropdown) + semua (layar kelola). */
export function useMetodePembayaran() {
  const [methods, setMethods] = useState([]);
  const [groups, setGroups] = useState({});
  const [methodsAll, setMethodsAll] = useState([]);
  const [groupsAll, setGroupsAll] = useState({});

  const load = () => {
    api.get("/payment-methods?active_only=true").then(r => {
      setMethods(r.data?.items || []);
      setGroups(r.data?.groups || {});
    }).catch(() => {});
    api.get("/payment-methods?active_only=false").then(r => {
      setMethodsAll(r.data?.items || []);
      setGroupsAll(r.data?.groups || {});
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  return { methods, groups, methodsAll, groupsAll, reload: load };
}

/* ---------------------------------------------------------------------------
 * Layar kelola metode + PEMETAAN COA-nya, satu tempat.
 *
 * Sebelumnya menambah metode butuh dua langkah di dua modul: admin membuat
 * metode di sini, lalu SPV/CEO memetakan akunnya di Finance > Pengaturan. Kalau
 * langkah kedua terlewat — dan itu sering — uang metode baru diam-diam mendarat
 * di Kas 1010 (UP.Gadget). Kolom "Uang masuk ke" di bawah menutup celah itu:
 * akunnya diatur di layar yang sama, dan metode yang belum punya pemetaan
 * memakai peringatan dari backend (bukan aturan kedua yang ditulis ulang di
 * sini, supaya layar & jurnal tidak bisa berbeda pendapat).
 * ------------------------------------------------------------------------- */
export function MetodePembayaranManager({ groups, onClose, onChange, warna = "emerald", idSuffix = "" }) {
  const [newForm, setNewForm] = useState({ name: "", group: "", note: "", account_id: "" });
  const [saving, setSaving] = useState(false);
  const [akun, setAkun] = useState([]);
  const [busyId, setBusyId] = useState("");
  const sfx = idSuffix ? `-${idSuffix}` : "";

  useEffect(() => {
    api.get("/finance/accounts/kas-bank")
      .then(r => setAkun(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setAkun([]));   // tanpa daftar akun, layar tetap bisa dipakai untuk nama/grup
  }, []);

  const create = async () => {
    if (!newForm.name.trim()) { toast.error("Nama metode wajib"); return; }
    setSaving(true);
    try {
      const r = await api.post("/payment-methods", newForm);
      // Metode TETAP dibuat walau akunnya belum dipilih — kasir tidak boleh
      // terhambat. Yang berubah: kejadian itu tidak lagi lewat diam-diam.
      if (r.data?.peringatan) toast.warning(r.data.peringatan, { duration: 8000 });
      else toast.success("Metode ditambahkan");
      setNewForm({ name: "", group: "", note: "", account_id: "" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const patch = async (m, payload, sukses) => {
    setBusyId(m.id);
    try {
      const r = await api.patch(`/payment-methods/${m.id}`, payload);
      if (r.data?.peringatan) toast.warning(r.data.peringatan, { duration: 8000 });
      else if (sukses) toast.success(sukses);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusyId(""); }
  };

  const toggleActive = (m) => patch(m, { is_active: !m.is_active });

  const rename = (m) => {
    const val = prompt(`Nama baru untuk "${m.name}":`, m.name);
    if (!val || !val.trim() || val.trim() === m.name) return;
    // Pemetaan COA-nya ikut karena backend menyimpannya per id, bukan per nama.
    patch(m, { name: val.trim() }, "Metode diperbarui — pemetaan akunnya ikut");
  };

  const setAkunMetode = (m, accountId) =>
    patch(m, { account_id: accountId }, accountId ? "Akun metode disimpan ✓" : "Kembali ke aturan bawaan");

  const nonaktifkan = async (m) => {
    if (!confirm(`Nonaktifkan metode "${m.name}"?\n\nMetode hilang dari dropdown, tapi TIDAK dihapus dari database — nota & jurnal lama yang memakainya tetap terbaca apa adanya.`)) return;
    setBusyId(m.id);
    try {
      const r = await api.delete(`/payment-methods/${m.id}`);
      toast.success(r.data?.message || "Metode dinonaktifkan");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusyId(""); }
  };

  const tone = warna === "violet"
    ? { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-900", btn: "bg-violet-600 hover:bg-violet-700" }
    : { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", btn: "bg-emerald-600 hover:bg-emerald-700" };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid={`methods-manager${sfx}`}>
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5" />
            <div>
              <h3 className="font-display font-semibold text-base">Kelola Metode Pembayaran</h3>
              <p className="text-[11px] text-slate-300">Tambah / edit / non-aktifkan metode &amp; tentukan rekening penerimanya</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Tambah baru — nama, kategori, DAN rekening penerima sekaligus */}
          <div className={`${tone.bg} border ${tone.border} rounded-lg p-3`}>
            <p className={`text-xs font-semibold ${tone.text} mb-2 inline-flex items-center gap-1`}>
              <Plus className="w-3 h-3" /> Tambah Metode Baru
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr,130px,190px,auto] gap-2">
              <input value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="Nama akun (mis. BCA Marketing)" data-testid={`input-new-method-name${sfx}`}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={newForm.group} onChange={e => setNewForm({ ...newForm, group: e.target.value })}
                placeholder="Kategori" list={`pm-groups${sfx}`} data-testid={`input-new-method-group${sfx}`}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              <datalist id={`pm-groups${sfx}`}>
                {Object.keys(groups).map(g => <option key={g} value={g} />)}
              </datalist>
              <select value={newForm.account_id} onChange={e => setNewForm({ ...newForm, account_id: e.target.value })}
                data-testid={`input-new-method-akun${sfx}`}
                className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">Uang masuk ke… (bawaan)</option>
                {akun.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
              <button onClick={create} disabled={saving} data-testid={`btn-save-new-method${sfx}`}
                className={`h-9 px-4 rounded-lg ${tone.btn} text-white text-xs font-medium disabled:opacity-50 whitespace-nowrap`}>
                {saving ? "..." : "+ Tambah"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Rekening boleh dikosongkan — tapi tanpa itu uangnya mengikuti aturan bawaan (nama berbau bank → rekening bank itu, selain itu → Kas Toko cabang).
            </p>
          </div>

          {/* Daftar per kategori */}
          {Object.entries(groups).map(([g, list]) => (
            <div key={g}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">{g}</p>
              <div className="space-y-1.5">
                {list.map(m => (
                  <div key={m.id}
                    className={`rounded-lg border p-2.5 ${m.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`}
                    data-testid={`method-row${sfx}-${m.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900">{m.name}</p>
                        {m.note && <p className="text-[10px] text-slate-500">{m.note}</p>}
                      </div>
                      <select value={m.account_id || ""} disabled={busyId === m.id}
                        onChange={e => setAkunMetode(m, e.target.value)}
                        title="Rekening/kas yang menerima uang metode ini"
                        data-testid={`method-akun${sfx}-${m.id}`}
                        className="h-8 px-2 rounded border border-slate-200 text-xs bg-white max-w-[220px] disabled:opacity-60">
                        <option value="">— ikuti bawaan —</option>
                        {akun.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                      </select>
                      <button onClick={() => toggleActive(m)} disabled={busyId === m.id}
                        title={m.is_active ? "Nonaktifkan" : "Aktifkan"}
                        className={`text-[10px] px-2 py-1 rounded font-medium disabled:opacity-50 ${m.is_active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}>
                        {m.is_active ? "Aktif" : "Nonaktif"}
                      </button>
                      <button onClick={() => rename(m)} disabled={busyId === m.id} title="Ganti nama"
                        className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700 disabled:opacity-50">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => nonaktifkan(m)} disabled={busyId === m.id || !m.is_active}
                        title="Nonaktifkan (tidak dihapus dari database)"
                        data-testid={`btn-del-method${sfx}-${m.id}`}
                        className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 disabled:opacity-30">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {m.akun_efektif?.account_code && (
                      <p className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">
                        <Landmark className="w-3 h-3" />
                        Uang masuk ke <span className="font-mono">{m.akun_efektif.account_code}</span> {m.akun_efektif.account_name}
                      </p>
                    )}
                    {m.peringatan && (
                      <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-flex items-start gap-1"
                        data-testid={`method-peringatan${sfx}-${m.id}`}>
                        <AlertTriangle className="w-3 h-3 mt-px shrink-0" /> {m.peringatan}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 p-4 flex justify-end">
          <button onClick={onClose} className="h-10 px-5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium">
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

/** Tombol pembuka layar kelola — dipakai identik di Sales, Nota Sales, Nota Akuisisi. */
export function TombolKelolaMetode({ jumlah, onClick, testid = "btn-manage-methods" }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium">
      <Settings className="w-4 h-4" /> Kelola Metode
      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-800 text-[10px] font-bold">{jumlah}</span>
    </button>
  );
}
